import type { SalaryStructure, SalaryStructureLine } from "../api/types";

export const DEDUCTIONS_EXCEED_GROSS_MESSAGE =
  "Total deductions cannot exceed gross pay.";

/** The value a line contributes, resolving a percentage against the gross anchor. */
export function lineAmount(line: SalaryStructureLine, gross: number): number {
  if (line.calculation_mode === "percent_of_gross") {
    return (gross * Number(line.percent_of_gross ?? 0)) / 100;
  }
  return Number(line.amount ?? 0);
}

export interface StructureTotals {
  /** The gross anchor percentages resolve against. */
  anchor: number;
  /** Total earnings. Falls back to the anchor only when no earnings are named. */
  gross: number;
  earnings: SalaryStructureLine[];
  deductions: SalaryStructureLine[];
  totalDeductions: number;
  /** Gross minus the structure's own deductions, before statutory lines. */
  estimatedNet: number;
}

/**
 * A structure's money from its own lines.
 *
 * ``gross_monthly`` is the anchor percentages resolve against. Gross pay is the
 * sum of the named earning components, so adding an earning actually raises
 * pay; an old structure that names no earnings still falls back to its anchor.
 * The estimate is gross minus the lines' deductions; the payroll engine layers
 * on PF/ESIC/professional tax from the employee's flags at compute time, so a
 * structure total is an estimate and never the final net.
 */
export function structureTotals(structure: SalaryStructure): StructureTotals {
  const anchor = Number(structure.gross_monthly?.amount ?? 0);
  const lines = structure.lines ?? [];
  const earnings = lines.filter((line) => line.component_kind === "earning");
  const deductions = lines.filter((line) => line.component_kind === "deduction");
  const earningsTotal = earnings.reduce((sum, line) => sum + lineAmount(line, anchor), 0);
  const gross = earnings.length > 0 ? earningsTotal : anchor;
  const totalDeductions = deductions.reduce((sum, line) => sum + lineAmount(line, anchor), 0);
  return {
    anchor,
    gross,
    earnings,
    deductions,
    totalDeductions,
    estimatedNet: gross - totalDeductions,
  };
}

/**
 * The save-blocking rule the API enforces. Deductions may equal gross (net
 * zero) but cannot exceed it. Compared in paise so floating-point noise from a
 * percentage line does not reject an exact boundary.
 */
export function validateDeductions(structure: SalaryStructure): string | null {
  const { anchor, totalDeductions } = structureTotals(structure);
  const grossPaise = Math.round(anchor * 100);
  const deductionPaise = Math.round(totalDeductions * 100);
  return deductionPaise > grossPaise ? DEDUCTIONS_EXCEED_GROSS_MESSAGE : null;
}
